/**
 * useFundingGauge - Headless Hook
 *
 * Computes category-specific funding metrics and progress from raw financial data.
 * Pure calculation logic with no UI concerns.
 *
 * Usage:
 * ```tsx
 * const state = useFundingGauge({
 *   financials: award.financials,
 *   category: award.category,
 *   type: award.type,
 * })
 * ```
 */
import { useMemo } from 'react'

import { formatCompactCurrency } from '../../../common/transforms'
import {
  getCategoryFundingConfig,
  normalizeCategoryForFunding,
} from './configs'
import type {
  ExtendedFinancialData,
  FundingGaugeCategory,
  FundingGaugeState,
  FundingMetric,
  MetricColor,
  ProgressConfig,
} from './types'

// ============================================================================
// Hook Input Types
// ============================================================================

export interface UseFundingGaugeOptions {
  /** Raw financial data from the award */
  financials: ExtendedFinancialData
  /** Award category (contract, grant, idv, etc.) */
  category: string | null | undefined
  /** Award type code (for loan disambiguation: 07=direct, 08=guaranteed) */
  type?: string | null
  /** IDV child order aggregation data (optional, for IDV awards) */
  childOrderData?: {
    obligated: number
    outlayed: number
    count: number
  }
}

// ============================================================================
// Metric Builders
// ============================================================================

/**
 * Build a single metric object.
 */
function buildMetric(
  id: string,
  label: string,
  value: number,
  color: MetricColor = 'primary',
  description?: string,
  isPrimary = false,
): FundingMetric {
  return {
    id,
    label,
    value,
    formattedValue: formatCompactCurrency(value) || '$0',
    color,
    description,
    isPrimary,
  }
}

/**
 * Build metrics for Contract category.
 */
function buildContractMetrics(f: ExtendedFinancialData): FundingMetric[] {
  return [
    buildMetric(
      'obligated',
      'Total Obligated',
      f.obligatedAmount,
      'accent',
      'Funded & Active',
      true,
    ),
    buildMetric(
      'current',
      'Current Award',
      f.currentAwardAmount,
      'primary',
      'Base + exercised options',
    ),
    buildMetric(
      'potential',
      'Potential Award',
      f.potentialAwardAmount,
      'muted',
      'If all options exercised',
    ),
    buildMetric(
      'outlayed',
      'Total Outlayed',
      f.totalOutlayedAmount,
      'success',
      'Actual disbursements',
    ),
  ]
}

/**
 * Build metrics for IDV category.
 */
function buildIdvMetrics(
  f: ExtendedFinancialData,
  childData?: { obligated: number; outlayed: number; count: number },
): FundingMetric[] {
  const ceiling = f.baseAndAllOptionsValue ?? f.potentialAwardAmount
  const currentValue = f.baseExercisedOptionsValue ?? f.currentAwardAmount

  const metrics: FundingMetric[] = [
    buildMetric(
      'ceiling',
      'Contract Ceiling',
      ceiling,
      'primary',
      'Maximum potential value',
      true,
    ),
    buildMetric(
      'currentValue',
      'Current Value',
      currentValue,
      'info',
      'With exercised options',
    ),
  ]

  // Add child order aggregations if available
  if (childData && childData.count > 0) {
    const orderLabel = childData.count === 1 ? 'order' : 'orders'
    metrics.push(
      buildMetric(
        'childObligated',
        'Orders Obligated',
        childData.obligated,
        'accent',
        `From ${childData.count} ${orderLabel}`,
      ),
      buildMetric(
        'childOutlayed',
        'Orders Outlayed',
        childData.outlayed,
        'success',
        'Actual disbursements',
      ),
    )
  }

  return metrics
}

/**
 * Build metrics for Grant category.
 */
function buildGrantMetrics(f: ExtendedFinancialData): FundingMetric[] {
  const metrics: FundingMetric[] = [
    buildMetric(
      'obligated',
      'Federal Obligation',
      f.obligatedAmount,
      'accent',
      'Committed federal funds',
      true,
    ),
    buildMetric(
      'award',
      'Total Award',
      f.currentAwardAmount,
      'primary',
      'Full award amount',
    ),
    buildMetric(
      'outlayed',
      'Federal Outlays',
      f.totalOutlayedAmount,
      'success',
      'Actual disbursements',
    ),
  ]

  // Only show non-federal funding if present
  if (f.nonFederalFunding > 0) {
    metrics.push(
      buildMetric(
        'nonFederal',
        'Non-Federal Funding',
        f.nonFederalFunding,
        'info',
        'Cost share/match',
      ),
    )
  }

  return metrics
}

/**
 * Build metrics for Direct Payment category.
 */
function buildDirectPaymentMetrics(f: ExtendedFinancialData): FundingMetric[] {
  // Direct payments may have award_amount = 0 but outlays > 0 for ongoing programs
  const authorized = f.awardAmount ?? f.currentAwardAmount

  return [
    buildMetric(
      'authorized',
      'Authorized',
      authorized,
      'primary',
      'Total authorized payment',
      true,
    ),
    buildMetric(
      'obligated_dp',
      'Obligated',
      f.obligatedAmount,
      'accent',
      'Federal commitment',
    ),
    buildMetric(
      'disbursed',
      'Disbursed',
      f.totalOutlayedAmount,
      'success',
      'Actual payments made',
    ),
  ]
}

/**
 * Build metrics for Direct Loan (Type 07) category.
 */
function buildDirectLoanMetrics(f: ExtendedFinancialData): FundingMetric[] {
  const loanValue = f.totalLoanValue ?? 0
  const subsidyCost = f.totalSubsidyCost ?? 0

  return [
    buildMetric(
      'loanValue',
      'Loan Value',
      loanValue,
      'primary',
      'Face value of loan',
      true,
    ),
    buildMetric(
      'subsidyCost',
      'Federal Subsidy',
      subsidyCost,
      'info',
      'NPV cost to government',
    ),
    buildMetric(
      'loanDisbursed',
      'Disbursed',
      f.totalOutlayedAmount,
      'success',
      'Loan disbursements',
    ),
  ]
}

/**
 * Build metrics for Guaranteed Loan (Type 08) category.
 */
function buildGuaranteedLoanMetrics(f: ExtendedFinancialData): FundingMetric[] {
  const loanValue = f.totalLoanValue ?? 0
  const guaranteedAmount = f.faceValueLoanGuarantee ?? 0

  return [
    buildMetric(
      'loanValue',
      'Loan Value',
      loanValue,
      'primary',
      'Face value of loan',
      true,
    ),
    buildMetric(
      'guaranteedAmount',
      'Guaranteed Amount',
      guaranteedAmount,
      'info',
      'Federal guarantee',
    ),
  ]
}

/**
 * Build metrics for Insurance category.
 */
function buildInsuranceMetrics(f: ExtendedFinancialData): FundingMetric[] {
  // Insurance has limited data in USASpending
  const coverage = f.awardAmount ?? f.currentAwardAmount

  return [
    buildMetric(
      'coverageAmount',
      'Coverage Amount',
      coverage,
      'primary',
      'Insurance coverage',
      true,
    ),
  ]
}

// ============================================================================
// Progress Calculation
// ============================================================================

/**
 * Calculate progress percentage safely.
 */
function calculateProgress(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0
  const percent = (numerator / denominator) * 100
  return Math.min(Math.max(percent, 0), 100) // Clamp 0-100
}

/**
 * Build progress configuration for a category.
 */
function buildProgressConfig(
  category: FundingGaugeCategory,
  f: ExtendedFinancialData,
): ProgressConfig {
  const config = getCategoryFundingConfig(category)

  if (!config.hasProgressBar) {
    return {
      showProgressBar: false,
      percentage: 0,
      label: '',
      color: 'primary',
    }
  }

  // Get numerator and denominator values
  const numeratorKey = config.progressNumerator
  const denominatorKey = config.progressDenominator

  if (!numeratorKey || !denominatorKey) {
    return {
      showProgressBar: false,
      percentage: 0,
      label: '',
      color: 'primary',
    }
  }

  const numerator = (f[numeratorKey] as number) ?? 0
  const denominator = (f[denominatorKey] as number) ?? 0
  const percentage = calculateProgress(numerator, denominator)

  // Determine color based on category
  let color: MetricColor = 'accent'
  if (category === 'grant' || category === 'direct_payment') {
    color = 'success' // Disbursement-focused categories
  } else if (category === 'direct_loan') {
    color = 'info' // Loan disbursement
  }

  return {
    showProgressBar: true,
    percentage,
    label: `${percentage.toFixed(1)}% ${config.progressLabel}`,
    subtitle: buildProgressSubtitle(category, percentage),
    color,
  }
}

/**
 * Build descriptive subtitle for progress bar.
 */
function buildProgressSubtitle(
  category: FundingGaugeCategory,
  percentage: number,
): string {
  switch (category) {
    case 'contract':
      return `${percentage.toFixed(0)}% of potential award obligated`
    case 'grant':
      return `${percentage.toFixed(0)}% of federal obligation disbursed`
    case 'direct_payment':
      return `${percentage.toFixed(0)}% of obligation disbursed`
    case 'direct_loan':
      return `${percentage.toFixed(0)}% of loan value disbursed`
    default:
      return ''
  }
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Headless hook that computes funding gauge state from raw financial data.
 *
 * @param options - Financial data, category, and optional child order data
 * @returns Computed state ready for presentation components
 */
export function useFundingGauge(
  options: UseFundingGaugeOptions,
): FundingGaugeState {
  const { financials, category: rawCategory, type, childOrderData } = options

  return useMemo(() => {
    // Normalize category
    const category = normalizeCategoryForFunding(rawCategory, type)
    const config = getCategoryFundingConfig(category)

    // Build metrics based on category
    let metrics: FundingMetric[]
    switch (category) {
      case 'contract':
        metrics = buildContractMetrics(financials)
        break
      case 'idv':
        metrics = buildIdvMetrics(financials, childOrderData)
        break
      case 'grant':
        metrics = buildGrantMetrics(financials)
        break
      case 'direct_payment':
        metrics = buildDirectPaymentMetrics(financials)
        break
      case 'direct_loan':
        metrics = buildDirectLoanMetrics(financials)
        break
      case 'guaranteed_loan':
        metrics = buildGuaranteedLoanMetrics(financials)
        break
      case 'insurance':
        metrics = buildInsuranceMetrics(financials)
        break
      default:
        metrics = buildContractMetrics(financials)
    }

    // Build progress
    const progress = buildProgressConfig(category, financials)

    // Build secondary progress (outlay rate for contracts)
    let secondaryProgress: FundingGaugeState['secondaryProgress']
    if (category === 'contract' && financials.obligatedAmount > 0) {
      const outlayRate = calculateProgress(
        financials.totalOutlayedAmount,
        financials.obligatedAmount,
      )
      secondaryProgress = {
        label: `${outlayRate.toFixed(1)}% Outlayed`,
        percentage: outlayRate,
      }
    }

    return {
      category,
      title: config.title,
      progress,
      metrics,
      note: config.note ?? null,
      secondaryProgress,
    }
  }, [financials, rawCategory, type, childOrderData])
}

export type { FundingGaugeState, FundingMetric, ProgressConfig }
